<script>
import { mapGetters } from "vuex";
import {
  enableChat,
  isMassiveEnabled,
  isAppointmentRelocationHiddenForBackoffice,
} from "@/services/config";

export default {
  name: "BaseSubNav",

  props: {
    navigationType: {
      type: String,
      default: null,
      validator: function (value) {
        return (
          value === null ||
          [
            "dashboard-alerts",
            "treatment",
            "appointment",
            "settings",
            "validation",
            "administration",
            "prescription",
          ].includes(value)
        );
      },
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isMassiveEnabled,
    };
  },

  computed: {
    ...mapGetters([
      "isAdmin",
      "isAssistant",
      "isDoctor",
      "isManager",
      "isPatient",
    ]),

    canSeeAppointmentRelocation() {
      if (this.isPatient) return false;
      if (
        isAppointmentRelocationHiddenForBackoffice &&
        (this.isAdmin || this.isDoctor || this.isAssistant || this.isManager)
      ) {
        return false;
      }
      return true;
    },

    enableChat() {
      return enableChat;
    },

    isTreatmentSubNavActive() {
      return (
        this.$route.name === "treatment" ||
        this.$route.name === "treatment-tasks"
      );
    },

    isConfigSubNavActive() {
      return (
        this.$route.name &&
        this.$route.name !== "treatment-tasks" &&
        (this.$route.name.includes("treatment-") ||
          this.$route.name === "create-vital" ||
          this.$route.name === "update-vital")
      );
    },
  },

  methods: {
    isRoute(name) {
      return this.$route.name === name;
    },
  },
};
</script>

<template lang="pug">
section.subnav-container
  .subnav
    .subnav__title-nav
      h1.headline__title
        slot(name="title")
      .subnav__list-wrapper(v-if="showNavigation")
        slot(name="navigation")
          //- Navegación de Seguimientos
          nav.subnav__list(v-if="navigationType === 'dashboard-alerts'")
            router-link.subnav__item(:to="{ name: 'dashboard-alerts' }") Todos
            router-link.subnav__item(
              v-if="isAdmin || isAssistant"
              :to="{ name: 'assistant-evaluation' }"
            ) Derivaciones Asesores
            router-link.subnav__item(
              v-if="isDoctor && enableChat"
              :to="{ name: 'dashboard-conversations' }"
            ) Conversaciones
            router-link.subnav__item(
              v-if="(isAdmin || isAssistant) && enableChat"
              :to="{ name: 'dashboard-conversations-unreplied' }"
            ) Auditoría Chat

          //- Navegación de Administración
          nav.subnav__list(v-if="navigationType === 'administration'")
            router-link.subnav__item(
              :to="{ name: 'administration' }"
              :class="{ 'router-link-exact-active': isRoute('administration') }"
              exact
            ) Administración
            router-link.subnav__item(
              :to="{ name: 'administration-history' }"
              :class="{ 'router-link-exact-active': isRoute('administration-history') }"
              exact
            ) Historial

          //- Navegación de Prescripciones
          nav.subnav__list(v-if="navigationType === 'prescription'")
            router-link.subnav__item(
              :to="{ name: 'prescription-queue' }"
              :class="{ 'router-link-exact-active': isRoute('prescription-queue') }"
              exact
            ) Administración
            router-link.subnav__item(
              :to="{ name: 'prescription-history' }"
              :class="{ 'router-link-exact-active': isRoute('prescription-history') }"
              exact
            ) Historial

          //- Navegación de Tratamientos
          nav.subnav__list(v-if="navigationType === 'treatment'")
            router-link.subnav__item(
              :to="{ name: 'treatment' }"
              :class="{ 'router-link-exact-active': isTreatmentSubNavActive }"
              exact
            ) Protocolos
            el-dropdown(trigger="click")
              a.el-dropdown-link.subnav__item(
                :class="{ 'router-link-exact-active': isConfigSubNavActive }"
                exact
              )
                | Configuración
                i.el-icon-arrow-down.el-icon--right
              el-dropdown-menu(slot="dropdown")
                el-dropdown-item(
                  :disabled="isRoute('treatment-medication')"
                  :class="{ active: isRoute('treatment-medication') }"
                )
                  router-link(:to="{ name: 'treatment-medication' }") Medicaciones
                el-dropdown-item(
                  :disabled="isRoute('treatment-appointment')"
                  :class="{ active: isRoute('treatment-appointment') }"
                )
                  router-link(:to="{ name: 'treatment-appointment' }") Recordatorios
                el-dropdown-item(
                  :disabled="isRoute('treatment-recommendation')"
                  :class="{ active: isRoute('treatment-recommendation') }"
                )
                  router-link(:to="{ name: 'treatment-recommendation' }") Recomendaciones
                el-dropdown-item(
                  :disabled="isRoute('treatment-exercise')"
                  :class="{ active: isRoute('treatment-exercise') }"
                )
                  router-link(:to="{ name: 'treatment-exercise' }") Ejercicios
                el-dropdown-item(
                  :disabled="isRoute('treatment-vital')"
                  :class="{ active: isRoute('treatment-vital') || isRoute('update-vital') }"
                )
                  router-link(:to="{ name: 'treatment-vital' }") Registros
                el-dropdown-item(
                  :disabled="isRoute('treatment-report')"
                  :class="{ active: isRoute('treatment-report') }"
                )
                  router-link(:to="{ name: 'treatment-report' }") Reportes
                el-dropdown-item(
                  :disabled="isRoute('treatment-checkin')"
                  :class="{ active: isRoute('treatment-checkin') }"
                )
                  router-link(:to="{ name: 'treatment-checkin' }") Check-in

          //- Navegación de Citas/Agenda
          nav.subnav__list(v-if="navigationType === 'appointment'")
            //- Dejo aqui comentado para solucionarlo despues
            //- router-link.subnav__item(
            //-   v-if="isDoctor"
            //-   :to="{ name: 'schedule' }"
            //-   :class="{ 'router-link-exact-active': isRoute('schedule') }"
            //-   exact
            //- ) Mi día
            router-link.subnav__item(
              :to="{ name: 'appointment-calendar' }"
              :class="{ 'router-link-exact-active': isRoute('appointment-calendar') }"
              exact
            ) Calendario
            router-link.subnav__item(
              v-if="isAdmin || isDoctor"
              :to="{ name: 'appointment-agendas' }"
              :class="{ 'router-link-exact-active': isRoute('appointment-agendas') }"
              exact
            ) {{ isDoctor ? "Mis agendas" : "Agendas" }}
            router-link.subnav__item(
              v-if="canSeeAppointmentRelocation"
              :to="{ name: 'appointment-relocation' }"
              :class="{ 'router-link-exact-active': isRoute('appointment-relocation') }"
              exact
            ) Citas a reprogramar

          //- Navegación de Validación
          nav.subnav__list(v-if="navigationType === 'validation'")
            router-link.subnav__item(
              :to="{ name: 'validation' }"
              :class="{ 'router-link-exact-active': isRoute('validation') }"
              exact
            ) Validar
            router-link.subnav__item(
              :to="{ name: 'validation-history' }"
              :class="{ 'router-link-exact-active': isRoute('validation-history') }"
              exact
            ) Historial

          //- Navegación de Configuración
          nav.subnav__list(v-if="navigationType === 'settings'")
            router-link.subnav__item(
              :to="{ name: 'settings' }"
              :class="{ 'router-link-exact-active': isRoute('settings') }"
              exact
            ) Administración
            router-link.subnav__item(
              v-if='isMassiveEnabled && isAdmin'
              :to="{ name: 'settings-massive' }",
              :class="{'active': isRoute('settings-massive')}"
              ) Carga Masiva
    .subnav__actions
      slot(name="actions")
</template>

<style scoped>
.subnav-container {
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 0 0 0 0;
}

.subnav {
  width: 100%;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  margin-top: 5px;
}

.subnav__title-nav {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.headline__title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #040055;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.headline__title small {
  font-size: 0.8rem;
  font-weight: normal;
  color: #666;
  margin-left: 0.5rem;
}

.search__input {
  min-width: 300px;
}

.subnav__list-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
}

.subnav__list {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  height: 100%;
}

::v-deep .subnav__item {
  flex: 0 0 auto;
  scroll-snap-align: left;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  color: #666;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-size: 0.9rem;

  &:hover {
    background: #f1f4f9;
    color: #040055;
  }

  &.router-link-active {
    background: #040055;
    color: white;
  }
}

.subnav__item.router-link-active,
.subnav__item.router-link-exact-active {
  border-bottom: 2px solid #040055;
  color: #040055;
  background: none;
}

.subnav__item:hover {
  color: #040055;
  background: none;
}

.el-dropdown-link.subnav__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.actions-container {
  display: flex;
  gap: 0.5rem;
}

.button--action {
  background: #040055;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2a2a8a;
  }
}

.subnav__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Responsive styles for headline__title */
@media (max-width: 768px) {
  .headline__title {
    font-size: 1.2rem;
  }

  .subnav__actions {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .headline__title {
    font-size: 1.3rem;
  }
}

@media (min-width: 1025px) {
  .headline__title {
    font-size: 1.2rem;
  }
}

::v-deep .el-switch__label {
  white-space: nowrap;
  font-size: 13px;
  padding: 0 4px;
}

::v-deep .el-dropdown-menu {
  .el-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 8px 16px;

    &.active {
      background: #f1f4f9;
      color: #040055;
    }

    router-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      color: inherit;
      width: 100%;
    }
  }
}
</style>
